/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { css, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

/**
 * The opacity of this button is controlled by the parent component
 */
const containerStyles = css({
	flexShrink: 0,
	marginLeft: 'auto',
	marginTop: token('space.negative.025'),
	marginBottom: token('space.negative.025'),
	display: 'inline-flex',
	opacity: 0,
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const containerStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		cursor: 'pointer',
		margin: 0,
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const placeholderStyles = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		cursor: 'default',
	},
});

export const EditPromptHoverTag = ({
	isClearMode = false,
	isPlaceholder,
}: {
	isClearMode?: boolean;
	// if true, will not show pointer cursor, only act as a space placeholder
	isPlaceholder?: boolean;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	const text = React.useMemo(() => {
		return isClearMode
			? formatMessage(messages.clearPromptHover)
			: formatMessage(messages.editPromptHover);
	}, [isClearMode, formatMessage]);

	return (
		<span
			id="edit-prompt-hover-button"
			data-testid="edit-prompt-hover-button"
			css={[containerStyles, containerStylesUnbounded, isPlaceholder && placeholderStyles]}
		>
			{fg('confluence_fronend_labels_categorization_migration') ? (
				<Tag text={text} isRemovable={false} />
			) : (
				<Tag text={text} isRemovable={false} />
			)}
		</span>
	);
};
